<script lang="ts" setup>
import type { NavItem } from '~kui/types/navigation';

import { NuxtLink } from '#components';
import { Button } from '~ds/components/ui/button';
import { ICONS } from '~ds/lib/icons';
import { useMotionAllowed } from '~kui/composables/useMotionPresets';
import { useNavPlaceIndicator } from '~kui/composables/useNavPlaceIndicator';
import { useUnifiedPublicLoaderContext } from '~kui/composables/useUnifiedPublicLoaderContext';

interface PublicNavItem extends NavItem {
  isSecondary?: boolean;

}

/**
 * The public site's top bar: the logo, the links in a pill, and the sign-in. `md` is the compact
 * bar with the sign-in inside the pill. `lg` sets the links at body size with room between them
 * and stands the sign-in beside the pill as a button of its own.
 *
 * The logo is handed to the public loader's context while the bar is mounted, because the intro
 * animation flies its own logo into exactly where this one sits. A ref rather than an id: a page
 * can draw this bar more than once (the coach draws it above the chat and again over a subpage),
 * and an id would then be in the document twice.
 */
interface Props {
  navItems: PublicNavItem[];
  homeLink?: string;
  logoLabel: string;
  logoIllustration?: 'anyway-logo' | 'anyway-logo-beta';
  /**
   * Keep the bar at its full height but show only the logo, the way the authed layout does.
   * For a page whose visitor has just signed in mid-flow: the links no longer apply, and
   * dropping the bar would shift everything below it.
   */
  logoOnly?: boolean;
  /** The signed-in visitor's in-app motion switch, on top of the OS preference. */
  animationsEnabled?: boolean;
  /** `lg` also marks the current place with a fill that slides between places; `md` marks it in weight only. */
  size?: 'md' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
  homeLink: '',
  logoIllustration: 'anyway-logo-beta',
  logoOnly: false,
  animationsEnabled: true,
  size: 'md',
});

const isNavOpen = ref(false);

const primaryNavItems = computed(() => props.navItems.filter(item => !item.isSecondary));
const secondaryNavItem = computed(() => props.navItems.find(item => item.isSecondary));
const hasPrimaryNavItems = computed(() => primaryNavItems.value.length > 0);
// At `lg` the sign-in leaves the pill to stand beside it.
const pillNavItems = computed(() => (props.size === 'lg' ? primaryNavItems.value : props.navItems));

// At `lg` the pill, the Sign in beside it and a student's stat pills share one height, so the bar
// reads the same signed in or out.
const pillClasses = computed(() => twMerge(cva('flex items-center gap-4 rounded-full', {
  variants: {
    size: {
      md: 'self-stretch',
      lg: 'relative h-12.5',
    },
    filled: {
      true: 'lg:bg-white p-1',
      false: '',
    },
  },
  compoundVariants: [{ size: 'lg', filled: true, class: 'px-2' }],
})({ size: props.size, filled: hasPrimaryNavItems.value })));

// The current page's link is set in the heavier weight, and at `lg` on an ink fill; `after`
// reserves that width on every link so the pill does not move as the weight changes. At `md` the
// router marks the exact route; at `lg` the item's own isActive does. Until the bar is mounted the
// link carries the fill and its light ink itself; after, the sliding indicator and its light labels
// do, so every link stays ink. At `lg` nothing fades when motion is off.
const linkVariants = cva('relative px-4 py-2 bg-transparent hover:bg-secondary-subtle rounded-full cursor-pointer text-nowrap after:content-[attr(data-text)] after:block after:h-0 after:overflow-hidden after:invisible after:pointer-events-none', {
  variants: {
    size: {
      md: 'text-sm font-medium after:font-semibold transition-all duration-300',
      lg: 'text-base after:font-medium hover:bg-muted',
    },
    motion: {
      true: '',
      false: '',
    },
    active: {
      true: '',
      false: '',
    },
    indicator: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { size: 'lg', active: false, class: 'font-normal' },
    { size: 'lg', active: true, class: 'font-medium' },
    { size: 'lg', active: true, indicator: false, class: 'bg-primary text-primary-foreground hover:bg-primary' },
    { size: 'lg', active: true, indicator: true, class: 'hover:bg-transparent' },
    { size: 'lg', motion: true, class: 'transition-all duration-300' },
  ],
});

const motionAllowed = useMotionAllowed(() => props.animationsEnabled);

const pill = useTemplateRef<HTMLElement>('pill');
const { rect: indicatorRect, slides, fillStyle, clipStyle } = useNavPlaceIndicator(pill, {
  enabled: () => props.size === 'lg',
  currentPlace: () => pillNavItems.value.find(item => item.isActive)?.to,
  motionAllowed,
});

const linkClasses = (active: boolean) => twMerge(linkVariants({
  size: props.size,
  active,
  indicator: indicatorRect.value !== null,
  motion: motionAllowed.value,
}));

// The ink fill behind the current link at `lg`, one element for the whole bar so moving between
// places slides it across rather than swapping two fills.
const indicatorClasses = computed(() => twMerge(cva('pointer-events-none absolute rounded-full bg-primary', {
  variants: {
    slides: {
      true: 'transition-all duration-300 ease-out',
      false: '',
    },
  },
})({ slides: slides.value })));

// The labels again in light ink, laid out exactly over the links and cut to the fill's shape, so
// whichever label the fill is under reads light and every other label ink. The text is the
// `before` content rather than a text node, so find-in-page and text queries see each label once.
const indicatorLabelsClasses = computed(() => twMerge(
  pillClasses.value,
  cva('pointer-events-none absolute inset-0 lg:bg-transparent', {
    variants: {
      slides: {
        true: 'transition-[clip-path] duration-300 ease-out',
        false: '',
      },
    },
  })({ slides: slides.value }),
));

const indicatorLabelClasses = (active: boolean) => twMerge(
  linkClasses(active),
  'text-primary-foreground before:content-[attr(data-text)]',
);

const drawerNavItems = computed(() => primaryNavItems.value.map(item => ({
  ...item,
  imagePath: item.imagePath ?? '/images/illustrations/rocket-blue.webp',
})));

const closeNav = () => {
  isNavOpen.value = false;
};

const handleItemClick = (item: NavItem) => {
  closeNav();
  if (item.onClick) {
    void item.onClick();
  }
};

const loaderCtx = useUnifiedPublicLoaderContext();
const navVisible = computed(() => !loaderCtx || loaderCtx.loaderComplete.value);

const logo = useTemplateRef<{ $el: Element | null }>('logo');
const logoElement = (): Element | null => (logo.value?.$el instanceof Element ? logo.value.$el : null);

onMounted(() => {
  if (loaderCtx && !loaderCtx.logoRef.value) {
    loaderCtx.logoRef.value = logoElement();
  }
});

onBeforeUnmount(() => {
  if (loaderCtx && loaderCtx.logoRef.value === logoElement()) {
    loaderCtx.logoRef.value = null;
  }
});
</script>

<template>
  <div
    class="px-2 pt-4 mb-4 w-full lg:bg-transparent transition-opacity duration-500"
    :class="[
      navVisible ? 'opacity-100' : 'opacity-0',
      hasPrimaryNavItems ? 'lg:mb-0 lg:px-0 lg:pt-0' : '',
    ]"
  >
    <header
      :class="twMerge(
        'w-full max-w-7xl mx-auto rounded-full transition-all duration-300 delay-300 bg-white',
        hasPrimaryNavItems ? 'px-4 py-2 lg:py-8 lg:rounded-none lg:bg-transparent' : 'px-6 py-3',
      )"
    >
      <div
        class="flex justify-between  "
      >
        <component
          :is="logoOnly ? 'span' : NuxtLink"
          v-bind="logoOnly ? {} : { 'to': homeLink, 'aria-label': logoLabel }"
          data-testid="public-nav-logo"
        >
          <KUICoreIllustration
            ref="logo"
            :illustration="logoIllustration"
            class="h-9 shrink-0 text-foreground"
            alt="Logo"
          />
        </component>
        <div v-if="!logoOnly" class="hidden items-center gap-4 lg:flex">
          <nav
            ref="pill"
            :aria-label="$t('unified.nav.mainAriaLabel')"
            :class="pillClasses"
          >
            <span
              v-if="indicatorRect"
              aria-hidden="true"
              :class="indicatorClasses"
              :style="fillStyle"
            />
            <template
              v-for="item in pillNavItems"
              :key="item.label"
            >
              <slot v-if="item.isSecondary" name="secondary">
                <NuxtLink
                  :to="item.to"
                  :data-text="item.label"
                  class="px-4 py-2 text-sm font-semibold bg-secondary rounded-full text-nowrap cursor-pointer"
                >
                  {{ item.label }}
                </NuxtLink>
              </slot>
              <NuxtLink
                v-else
                :to="item.to"
                :data-text="item.label"
                :class="linkClasses(size === 'lg' && Boolean(item.isActive))"
                v-bind="size === 'lg'
                  ? (item.isActive ? { 'aria-current': 'page', 'data-current-place': '' } : {})
                  : { 'exact-active-class': 'font-semibold' }"
              >
                {{ item.label }}
              </NuxtLink>
            </template>
            <div
              v-if="indicatorRect"
              aria-hidden="true"
              :class="indicatorLabelsClasses"
              :style="clipStyle"
            >
              <span
                v-for="item in primaryNavItems"
                :key="item.label"
                :data-text="item.label"
                :class="indicatorLabelClasses(Boolean(item.isActive))"
              />
            </div>
          </nav>
          <template v-if="size === 'lg' && secondaryNavItem">
            <slot name="secondary">
              <KUICoreClickable
                :to="secondaryNavItem.to"
                class="inline-flex h-12.5 items-center rounded-full bg-secondary px-6 text-base font-semibold text-nowrap text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="public-nav-sign-in"
                @click="handleItemClick(secondaryNavItem)"
              >
                {{ secondaryNavItem.label }}
              </KUICoreClickable>
            </slot>
          </template>
          <slot name="actions" />
        </div>
        <div v-if="!logoOnly" class="flex items-center gap-2 lg:hidden">
          <slot v-if="$slots.secondary" name="secondary" />
          <NuxtLink
            v-else-if="!hasPrimaryNavItems && secondaryNavItem"
            :to="secondaryNavItem.to"
            class="px-4 py-2 text-sm font-semibold bg-secondary rounded-full text-nowrap cursor-pointer"
            @click="handleItemClick(secondaryNavItem)"
          >
            {{ secondaryNavItem.label }}
          </NuxtLink>
          <KUIUnifiedNavigationDrawer
            v-if="hasPrimaryNavItems"
            v-model:open="isNavOpen"
            :nav-items="drawerNavItems"
            :animations-enabled="animationsEnabled"
            @item-click="closeNav"
          >
            <template #trigger>
              <KUICoreButtonIcon
                :icon="ICONS.menuSolid"
                variant="white"
                :aria-label="$t('unified.nav.menuButton')"
              />
            </template>
            <Button
              v-if="secondaryNavItem && !$slots.secondary"
              variant="secondary"
              class="mx-auto"
              size="lg"
              as-child
              @click="handleItemClick(secondaryNavItem)"
            >
              <NuxtLink :to="secondaryNavItem.to">
                {{ secondaryNavItem.label }}
              </NuxtLink>
            </Button>
            <slot name="drawer" :close="closeNav" />
            <template v-if="$slots.drawerFooter" #footer>
              <slot name="drawerFooter" :close="closeNav" />
            </template>
          </KUIUnifiedNavigationDrawer>
        </div>
      </div>
    </header>
  </div>
</template>
